import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, ArrowRight, Mail, Loader2, AlertTriangle } from 'lucide-react';
import PillarLayout from './pillar/PillarLayout';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const BASE_URL = 'https://enbet-consulting.de';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pollCount, setPollCount] = useState(0);

  // Polling: Webhook kann etwas brauchen — wir pollen max 30s alle 3s nach 'paid'-Status
  useEffect(() => {
    if (!orderId) {
      setError('Keine Order-ID übergeben.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;

    const fetchStatus = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/public/checkout/status/${orderId}`);
        if (cancelled) return;
        setOrder(data);
        if (data.status === 'paid') {
          setLoading(false);
          return;
        }
        attempts += 1;
        setPollCount(attempts);
        if (attempts < maxAttempts && (data.status === 'pending' || !data.status)) {
          setTimeout(fetchStatus, 3000);
        } else {
          setLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.detail || 'Status konnte nicht geladen werden.');
        setLoading(false);
      }
    };
    fetchStatus();
    return () => { cancelled = true; };
  }, [orderId]);

  const canonical = `${BASE_URL}/checkout/success`;
  const isPaid = order?.status === 'paid';
  const isPending = order?.status === 'pending' || (!order && loading);

  return (
    <PillarLayout
      title="Buchung bestätigt | ENBET Consulting"
      description="Bestätigung deiner Buchung bei ENBET Consulting."
      canonical={canonical}
    >
      <section className="bg-white pt-32 pb-20 md:pt-40 md:pb-28 min-h-[70vh]">
        <div className="max-w-[720px] mx-auto px-6 md:px-10">
          {loading && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
              <Loader2 size={40} className="text-[#658a07] mx-auto mb-6 animate-spin" />
              <h1 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Bezahlung wird bestätigt…
              </h1>
              <p className="text-[#1f1f1f]/60 text-base" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Wir warten auf die Bestätigung deines Zahlungsanbieters. Das dauert normalerweise nur wenige Sekunden.
                {pollCount > 3 && <><br /><span className="text-xs">(Versuch {pollCount}/10)</span></>}
              </p>
            </motion.div>
          )}

          {!loading && error && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
              <AlertTriangle size={40} className="text-[#e07a2f] mx-auto mb-6" />
              <h1 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Status konnte nicht geladen werden
              </h1>
              <p className="text-[#1f1f1f]/60 text-base mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {error}<br />
                Falls deine Bezahlung erfolgreich war, bekommst du in jedem Fall die Bestätigung per E-Mail. Bei Fragen melde dich bei <a href="mailto:info@enbet-consulting.de" className="text-[#658a07] underline">info@enbet-consulting.de</a>.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#befa0a] px-6 py-3 text-xs uppercase tracking-wider font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Zur Startseite
              </Link>
            </motion.div>
          )}

          {!loading && isPaid && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="text-center mb-10">
                <CheckCircle size={56} className="text-[#658a07] mx-auto mb-6" />
                <p className="text-[#658a07] text-xs uppercase tracking-[0.3em] mb-3 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Zahlung erfolgreich
                </p>
                <h1 className="text-[#1f1f1f] text-3xl md:text-5xl font-bold uppercase tracking-tight leading-tight mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Danke, {order.customer_name?.split(' ')[0] || order.customer_name}.
                </h1>
                <p className="text-[#1f1f1f]/70 text-base md:text-lg max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Deine Buchung über <strong>{order.product_name}</strong> ({order.amount_eur} €) ist bestätigt.
                  Eine Bestätigungs-E-Mail ist gerade bei dir unterwegs.
                </p>
              </div>

              <div className="border border-[#e5e5e5] bg-[#f7f7f7] p-6 md:p-8 mb-8">
                <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Was jetzt passiert
                </p>
                {order.module === 'ready-check' ? (
                  <ol className="space-y-3 text-[#1f1f1f]/75 text-base leading-relaxed list-decimal list-inside" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    <li>Sebastian Funke meldet sich innerhalb von <strong>24 Stunden</strong> persönlich per Mail bei dir.</li>
                    <li>Du bekommst einen privaten Link zum <strong>KI Ready Check Diagnose-Chatbot</strong> — dort erhebst du in 30–45 Minuten die Ausgangslage deines Betriebs.</li>
                    <li>Sebastian erstellt deinen <strong>Audit-Report</strong> in 5–7 Tagen, danach persönliche Übergabe.</li>
                    <li>Die Rechnung (USt-konform) bekommst du in den nächsten Werktagen per Mail.</li>
                  </ol>
                ) : order.module === 'strategy' ? (
                  <ol className="space-y-3 text-[#1f1f1f]/75 text-base leading-relaxed list-decimal list-inside" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    <li>Sebastian Funke meldet sich innerhalb von <strong>24 Stunden</strong> zur <strong>Workshop-Terminvereinbarung</strong>.</li>
                    <li>1–2 Wochen Ausarbeitung, basierend auf deinem KI Ready Check.</li>
                    <li>Interaktiver Workshop-Tag mit deinen Entscheidern.</li>
                    <li>Die Rechnung (USt-konform) bekommst du in den nächsten Werktagen per Mail.</li>
                  </ol>
                ) : (
                  <p className="text-[#1f1f1f]/75 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Sebastian Funke meldet sich innerhalb von 24 Stunden persönlich bei dir.
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/" className="inline-flex items-center justify-center gap-2 bg-[#0a0a0a] text-[#befa0a] px-6 py-3 text-xs uppercase tracking-wider font-bold hover:bg-[#befa0a] hover:text-[#0a0a0a] transition-colors" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Zur Startseite <ArrowRight size={14} />
                </Link>
                <a href="mailto:info@enbet-consulting.de" className="inline-flex items-center justify-center gap-2 border border-[#1f1f1f]/30 text-[#1f1f1f] px-6 py-3 text-xs uppercase tracking-wider font-bold hover:bg-[#1f1f1f] hover:text-white transition-colors" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  <Mail size={14} /> Direkt antworten
                </a>
              </div>

              <p className="text-[#1f1f1f]/40 text-[11px] text-center mt-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Order-Referenz: <code className="text-[#658a07]">{order.id?.slice(0, 8)}</code>
              </p>
            </motion.div>
          )}

          {!loading && !isPaid && !error && order && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
              <Clock size={40} className="text-[#e07a2f] mx-auto mb-6" />
              <h1 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Bezahlung wird verarbeitet
              </h1>
              <p className="text-[#1f1f1f]/60 text-base md:text-lg max-w-xl mx-auto mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Deine Zahlung ist eingeleitet, aber noch nicht final bestätigt. Bei SEPA-Lastschrift oder Banküberweisung kann das einige Stunden dauern.
                Du bekommst die Bestätigung automatisch per E-Mail, sobald die Zahlung durch ist.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#befa0a] px-6 py-3 text-xs uppercase tracking-wider font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Zur Startseite <ArrowRight size={14} />
              </Link>
              <p className="text-[#1f1f1f]/40 text-[11px] mt-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Order-Referenz: <code className="text-[#658a07]">{order.id?.slice(0, 8)}</code>
              </p>
            </motion.div>
          )}
        </div>
      </section>
    </PillarLayout>
  );
}
